import React, { useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Loader2,
  Mail,
  Phone,
  Instagram,
  Tag,
  TrendingUp,
  Wallet,
  Copy,
  Pencil,
  XIcon,
  Inbox,
  ImageDown,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/lib/supabase";
import { apiGet } from "@/lib/api";
import { formatCurrency } from "@/lib/commission";
import { isTierId, type InfluencerTier } from "@/lib/tiers";
import { InfluencerTierBadge } from "../tiers/InfluencerTierBadge";
import { TierBenefitsCard } from "../tiers/TierBenefitsCard";
import { copyCoupon, importInstagramAvatar } from "./utils";
import {
  currentMonthKey,
  isActive,
  type InfluencerRecord,
} from "./hooks/useInfluencersData";

interface WithdrawalHistoryRow {
  id: string;
  amount: number;
  status: "pending" | "completed" | "rejected";
  created_at: string;
}

const STATUS_BADGE: Record<WithdrawalHistoryRow["status"], { label: string; className: string }> = {
  pending: { label: "Pendente", className: "bg-amber-400/10 text-amber-300 border-amber-400/25" },
  completed: { label: "Pago", className: "bg-green-400/10 text-green-300 border-green-400/25" },
  rejected: { label: "Cancelado", className: "bg-red-400/10 text-red-300 border-red-400/25" },
};

const PT_MONTHS_SHORT: Record<string, string> = {
  "01": "Jan", "02": "Fev", "03": "Mar", "04": "Abr", "05": "Mai", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Set", "10": "Out", "11": "Nov", "12": "Dez",
};

function monthLabel(monthYear: string): string {
  const [year, month] = monthYear.split("-");
  return `${PT_MONTHS_SHORT[month] || month}/${year.slice(2)}`;
}

function initials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

const MiniStat: React.FC<{ label: string; value: string; valueClassName?: string }> = ({
  label,
  value,
  valueClassName = "",
}) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className={`font-semibold tabular-nums ${valueClassName}`}>{value}</p>
  </div>
);

interface InfluencerDetailDrawerProps {
  influencer: InfluencerRecord | null;
  onOpenChange: (open: boolean) => void;
  /** Open the tier selector for this influencer (rendered by the parent). */
  onChangeTier: (record: InfluencerRecord) => void;
  /** Notify the parent that the import endpoint persisted a new avatar. */
  onAvatarImported: (userId: string, avatarUrl: string) => void;
}

/**
 * Right-side drawer: a mini dashboard for one influencer — overview,
 * performance (with a monthly trend), tier, and financial breakdown.
 * Self-contained data loading so it is reusable outside the Influencers tab.
 */
export const InfluencerDetailDrawer: React.FC<InfluencerDetailDrawerProps> = ({
  influencer,
  onOpenChange,
  onChangeTier,
  onAvatarImported,
}) => {
  const [withdrawals, setWithdrawals] = useState<WithdrawalHistoryRow[] | null>(null);
  const [tiers, setTiers] = useState<InfluencerTier[] | null>(null);
  const [importingAvatar, setImportingAvatar] = useState(false);

  const handleImportAvatar = async () => {
    if (!influencer || importingAvatar) return;
    setImportingAvatar(true);
    try {
      const avatarUrl = await importInstagramAvatar(influencer.id, !!influencer.avatar_url);
      onAvatarImported(influencer.id, avatarUrl);
      toast.success("Avatar atualizado!");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível importar");
    } finally {
      setImportingAvatar(false);
    }
  };

  const influencerId = influencer?.id ?? null;

  useEffect(() => {
    if (!influencerId) {
      setWithdrawals(null);
      return;
    }
    const loadWithdrawals = async () => {
      const { data, error } = await supabase
        .from("withdrawal_requests")
        .select("id, amount, status, created_at")
        .eq("user_id", influencerId)
        .order("created_at", { ascending: false })
        .limit(10);
      if (!error && data) setWithdrawals(data as WithdrawalHistoryRow[]);
      else setWithdrawals([]);
    };
    loadWithdrawals();
  }, [influencerId]);

  useEffect(() => {
    if (!influencer || tiers) return;
    const loadTiers = async () => {
      try {
        const res = await apiGet("/api/influencers/tier");
        if (res.ok) setTiers(await res.json());
      } catch (err) {
        console.error("Tier load error:", err);
      }
    };
    loadTiers();
  }, [influencer, tiers]);

  const performance = useMemo(() => {
    if (!influencer) return null;
    const cur = influencer.months[currentMonthKey()];
    let totalSales = 0;
    let totalCommission = 0;
    let totalOrders = 0;
    for (const m of Object.values(influencer.months)) {
      totalSales += m.total_sales || 0;
      totalCommission += m.commission_amount || 0;
      totalOrders += m.orders_count || 0;
    }
    const pending = influencer.total_withdrawn_pending;
    const withdrawn = influencer.total_withdrawn_completed;
    return {
      totalSales,
      totalCommission,
      totalOrders,
      monthSales: cur?.total_sales || 0,
      monthOrders: cur?.orders_count || 0,
      available: Math.max(0, totalCommission - pending - withdrawn),
      pending,
      withdrawn,
    };
  }, [influencer]);

  const trendData = useMemo(() => {
    if (!influencer) return [];
    return Object.entries(influencer.months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, data]) => ({ label: monthLabel(month), vendas: data.total_sales || 0 }));
  }, [influencer]);

  const currentTier = useMemo(() => {
    if (!influencer || !tiers || !isTierId(influencer.tier_id)) return null;
    return tiers.find((t) => t.id === influencer.tier_id) || null;
  }, [influencer, tiers]);

  // Financial breakdown shares for the stacked bar
  const breakdown = useMemo(() => {
    if (!performance) return null;
    const total = performance.available + performance.pending + performance.withdrawn;
    if (total <= 0) return null;
    return {
      available: (performance.available / total) * 100,
      pending: (performance.pending / total) * 100,
      withdrawn: (performance.withdrawn / total) * 100,
    };
  }, [performance]);

  return (
    <DialogPrimitive.Root open={!!influencer} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60" />
        <DialogPrimitive.Content
          className="drawer-content fixed right-0 top-0 z-50 h-full w-full sm:max-w-md border-l border-white/10 bg-background shadow-2xl overflow-y-auto focus:outline-none"
          aria-describedby={undefined}
        >
          {influencer && performance && (
            <div className="p-5 space-y-5">
              {/* Header */}
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-14 w-14 border border-white/10 shrink-0">
                      {influencer.avatar_url && <AvatarImage src={influencer.avatar_url} alt="" />}
                      <AvatarFallback className="bg-white/10 text-base font-semibold">
                        {initials(influencer.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <DialogPrimitive.Title className="text-lg font-bold truncate">
                        {influencer.name || "—"}
                      </DialogPrimitive.Title>
                      <div className="flex items-center gap-2 flex-wrap">
                        <InfluencerTierBadge tierId={influencer.tier_id} />
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className={`h-1.5 w-1.5 rounded-full ${isActive(influencer) ? "bg-green-400" : "bg-white/20"}`} />
                          <span className={isActive(influencer) ? "text-green-400" : "text-muted-foreground"}>
                            {isActive(influencer) ? "Ativa" : "Inativa"}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <DialogPrimitive.Close className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors shrink-0">
                    <XIcon className="h-4 w-4" />
                    <span className="sr-only">Fechar</span>
                  </DialogPrimitive.Close>
                </div>

                {/* Quick actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {influencer.coupon && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCoupon(influencer.coupon)}
                      className="border-white/10 bg-white/5 hover:bg-white/10 text-xs h-8"
                    >
                      <Tag className="h-3 w-3 mr-1.5 text-primary" />
                      <span className="font-bold tracking-wider">{influencer.coupon}</span>
                      <Copy className="h-3 w-3 ml-1.5 text-muted-foreground" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onChangeTier(influencer)}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-xs h-8"
                  >
                    <Pencil className="h-3 w-3 mr-1.5" />
                    Alterar categoria
                  </Button>
                  {influencer.social_media && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleImportAvatar}
                      disabled={importingAvatar}
                      className="border-white/10 bg-white/5 hover:bg-white/10 text-xs h-8"
                    >
                      {importingAvatar ? (
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <ImageDown className="h-3 w-3 mr-1.5" />
                      )}
                      {influencer.avatar_url ? "Atualizar avatar" : "Importar do Instagram"}
                    </Button>
                  )}
                </div>

                <div className="space-y-1 text-xs text-muted-foreground">
                  {influencer.email && (
                    <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{influencer.email}</p>
                  )}
                  {influencer.phone && (
                    <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{influencer.phone}</p>
                  )}
                  {influencer.social_media && (
                    <p className="flex items-center gap-2"><Instagram className="h-3.5 w-3.5" />{influencer.social_media}</p>
                  )}
                </div>
              </div>

              {/* Performance */}
              <section>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <TrendingUp className="h-4 w-4 text-primary" /> Performance
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <MiniStat label="Vendas totais" value={formatCurrency(performance.totalSales)} />
                  <MiniStat label="Vendas (mês)" value={formatCurrency(performance.monthSales)} valueClassName="text-primary" />
                  <MiniStat label="Pedidos (total / mês)" value={`${performance.totalOrders} / ${performance.monthOrders}`} />
                  <MiniStat label="Comissões geradas" value={formatCurrency(performance.totalCommission)} />
                </div>

                {trendData.length >= 2 && (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
                    <p className="text-[11px] text-muted-foreground mb-1">Vendas — últimos meses</p>
                    <div className="h-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                          <defs>
                            <linearGradient id="drawer-trend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.18} />
                              <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis
                            dataKey="label"
                            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis hide domain={[0, "auto"]} />
                          <Tooltip
                            cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
                            content={({ active, payload, label }) =>
                              active && payload?.length ? (
                                <div className="rounded-md border border-white/10 bg-background px-2.5 py-1.5 text-xs shadow-lg">
                                  <p className="text-muted-foreground">{label}</p>
                                  <p className="font-semibold tabular-nums">{formatCurrency(Number(payload[0].value))}</p>
                                </div>
                              ) : null
                            }
                          />
                          <Area
                            type="monotone"
                            dataKey="vendas"
                            stroke="var(--primary)"
                            strokeWidth={2}
                            fill="url(#drawer-trend)"
                            dot={false}
                            activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--background)" }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </section>

              {/* Tier */}
              <section>
                <h4 className="text-sm font-semibold mb-2">Categoria</h4>
                {currentTier ? (
                  <TierBenefitsCard tier={currentTier} />
                ) : (
                  <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-2">Nenhuma categoria atribuída.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onChangeTier(influencer)}
                      className="border-white/10 bg-white/5 hover:bg-white/10 text-xs h-7"
                    >
                      Atribuir categoria
                    </Button>
                  </div>
                )}
              </section>

              {/* Financial */}
              <section>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Wallet className="h-4 w-4 text-primary" /> Financeiro
                </h4>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <MiniStat label="Disponível" value={formatCurrency(performance.available)} valueClassName="text-green-400" />
                  <MiniStat label="Pendente" value={formatCurrency(performance.pending)} valueClassName="text-amber-300" />
                  <MiniStat label="Sacado" value={formatCurrency(performance.withdrawn)} />
                </div>

                {breakdown && (
                  <div className="space-y-1.5 mb-3">
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-white/5">
                      <div className="bg-green-400/80" style={{ width: `${breakdown.available}%` }} />
                      <div className="bg-amber-400/80 border-l-2 border-background" style={{ width: `${breakdown.pending}%` }} />
                      <div className="bg-white/25 border-l-2 border-background" style={{ width: `${breakdown.withdrawn}%` }} />
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-400/80" />Disponível</span>
                      <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400/80" />Pendente</span>
                      <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-white/25" />Sacado</span>
                    </div>
                  </div>
                )}

                <p className="text-xs font-medium text-muted-foreground mb-1.5">Últimos saques</p>
                {withdrawals === null ? (
                  <div className="flex justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                ) : withdrawals.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] py-5 text-center">
                    <Inbox className="h-5 w-5 mx-auto text-muted-foreground/40 mb-1" />
                    <p className="text-xs text-muted-foreground">Nenhum saque registrado.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-white/10 overflow-hidden divide-y divide-white/5">
                    {withdrawals.map((w) => {
                      const badge = STATUS_BADGE[w.status];
                      return (
                        <div key={w.id} className="flex items-center justify-between gap-2 bg-white/[0.03] px-3 py-2 text-xs">
                          <span className="text-muted-foreground tabular-nums">
                            {format(new Date(w.created_at), "dd MMM yyyy")}
                          </span>
                          <span className="font-semibold tabular-nums">{formatCurrency(w.amount)}</span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default InfluencerDetailDrawer;
